/**
 * localStorage key used to store the currently simulated user identity for development testing.
 */
export const SIMULATE_USER_KEY = 'prism_simulated_user';

/**
 * localStorage key used to store the currently simulated role (id or "AppKey.RoleName") for development testing.
 */
export const SIMULATE_ROLE_KEY = 'prism_simulated_role';

function notifySimulationChanged(): void {
  window.dispatchEvent(new Event('prism_simulation_changed'));
  // ArcGIS creates module-level layer instances and may retain request state, so a full reload is
  // the only reliable way to ensure layers created before the simulation switch are discarded.
  window.setTimeout(() => window.location.reload(), 0);
}

/**
 * Retrieves the currently simulated user (email or ID) from localStorage.
 *
 * @returns The simulated user string if set, or null.
 */
export function getSimulatedUser(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SIMULATE_USER_KEY);
}

/**
 * Retrieves the currently simulated role (id or "AppKey.RoleName") from localStorage.
 *
 * @returns The simulated role string if set, or null.
 */
export function getSimulatedRole(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SIMULATE_ROLE_KEY);
}

/**
 * Sets or clears the simulated user in localStorage, clears any simulated role
 * (user and role simulation are mutually exclusive), and dispatches a
 * 'prism_simulation_changed' event.
 *
 * @param userEmailOrId - The user email or ID to simulate, or null/falsy to stop simulating.
 */
export function setSimulatedUser(userEmailOrId: string | null): void {
  if (typeof window === 'undefined') return;

  if (userEmailOrId) {
    localStorage.setItem(SIMULATE_USER_KEY, userEmailOrId);
    localStorage.removeItem(SIMULATE_ROLE_KEY);
  } else {
    localStorage.removeItem(SIMULATE_USER_KEY);
  }

  notifySimulationChanged();
}

/**
 * Sets or clears the simulated role in localStorage, clears any simulated user
 * (user and role simulation are mutually exclusive), and dispatches a
 * 'prism_simulation_changed' event.
 *
 * @param roleIdOrKey - The role id, or "AppKey.RoleName" (e.g. "Prism.Prism Executive"), or null/falsy to stop simulating.
 */
export function setSimulatedRole(roleIdOrKey: string | null): void {
  if (typeof window === 'undefined') return;

  if (roleIdOrKey) {
    localStorage.setItem(SIMULATE_ROLE_KEY, roleIdOrKey);
    localStorage.removeItem(SIMULATE_USER_KEY);
  } else {
    localStorage.removeItem(SIMULATE_ROLE_KEY);
  }

  notifySimulationChanged();
}

/**
 * Clears the simulated user setting from localStorage.
 */
export function clearSimulatedUser(): void {
  setSimulatedUser(null);
}

/**
 * Clears the simulated role setting from localStorage.
 */
export function clearSimulatedRole(): void {
  setSimulatedRole(null);
}

/**
 * Clears both simulated user and simulated role settings from localStorage,
 * dispatching a single 'prism_simulation_changed' event.
 */
export function clearAllSimulation(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SIMULATE_USER_KEY);
  localStorage.removeItem(SIMULATE_ROLE_KEY);
  notifySimulationChanged();
}
